import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  getMsActivation, requestMsActivation, acceptMsFees, payMsActivation, getMsPaymentStatus, formatApiError,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import {
  Cloud, LifeBuoy, Info, Loader2, CheckCircle2, Clock, FileSignature, CreditCard,
  AlertTriangle, ShieldCheck,
} from "lucide-react";

const STATUS_STYLES = {
  NOT_REQUESTED: "bg-white/10 border-white/20 text-white/60",
  REQUESTED: "bg-sky-500/15 border-sky-500/40 text-sky-300",
  UNDER_REVIEW: "bg-sky-500/15 border-sky-500/40 text-sky-300",
  QUOTE_REQUIRED: "bg-amber-500/15 border-amber-500/40 text-amber-300",
  AWAITING_CUSTOMER_APPROVAL: "bg-[#EAB308]/15 border-[#EAB308]/40 text-[#EAB308]",
  PAYMENT_REQUIRED: "bg-[#EAB308]/15 border-[#EAB308]/40 text-[#EAB308]",
  APPROVED: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300",
  ACTIVE: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300",
  SUSPENDED: "bg-[#dc143c]/15 border-[#dc143c]/40 text-[#ff8fa3]",
  REJECTED: "bg-[#dc143c]/15 border-[#dc143c]/40 text-[#ff8fa3]",
};

const MODULES = ["CORE", "ERP", "PUNCH", "CRM", "COMPTA", "PAIE", "DEVIS_AI", "TAKE_OFF", "SUPPORT", "MOBILE"];
const SERVICES = [
  { v: "ONEDRIVE_PERSONAL", l: "Microsoft OneDrive" },
  { v: "ONEDRIVE_BUSINESS", l: "OneDrive Entreprise" },
  { v: "SHAREPOINT", l: "Microsoft SharePoint" },
];

// Renvoie true quand le statut est ACTIVE (le parent affiche alors la connexion Microsoft)
export default function MsActivation({ onActive }) {
  const { user } = useAuth();
  const canManage = user && ["owner", "admin", "platform_owner"].includes(user.role);
  const [act, setAct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [mods, setMods] = useState(["CORE"]);
  const [svcs, setSvcs] = useState(["ONEDRIVE_BUSINESS"]);
  const [fees, setFees] = useState({ info: false, setup: false, recurring: false, authorize: false, name: user?.name || "" });

  const load = async () => {
    try {
      const d = await getMsActivation();
      setAct(d);
      if (d.status === "ACTIVE") onActive?.(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Retour de paiement Stripe
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("activation") === "paid" && sp.get("session_id")) {
      getMsPaymentStatus(sp.get("session_id")).then((r) => {
        if (r.paid) toast.success("Paiement reçu. Le soutien technique finalise l'activation.");
        setAct(r.activation);
      }).catch(() => {}).finally(() => {
        window.history.replaceState({}, "", "/app/cloud-connect");
      });
    } else if (sp.get("activation") === "cancelled") {
      toast.info("Paiement annulé.");
      window.history.replaceState({}, "", "/app/cloud-connect");
    }
  }, []);

  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const submitRequest = async () => {
    setBusy(true);
    try {
      const d = await requestMsActivation({ requested_modules: mods, requested_services: svcs });
      setAct(d);
      toast.success("Demande transmise au soutien technique.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const submitFees = async () => {
    setBusy(true);
    try {
      const d = await acceptMsFees({
        accept_info: fees.info, accept_setup: fees.setup, accept_recurring: fees.recurring,
        authorize_activation: fees.authorize, signer_name: fees.name,
      });
      setAct(d);
      if (d.status === "PAYMENT_REQUIRED") toast.success("Frais acceptés. Procédez au paiement.");
      else toast.success("Frais acceptés. Activation en cours par le soutien.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const pay = async () => {
    setBusy(true);
    try {
      const d = await payMsActivation(window.location.origin);
      window.location.href = d.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-[#EAB308] animate-spin" /></div>;

  const status = act?.status || "NOT_REQUESTED";
  if (status === "ACTIVE") return null; // le parent gère l'écran de connexion

  const Badge = () => (
    <span data-testid="ms-status-badge"
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${STATUS_STYLES[status]}`}>
      {act?.status_label || "Non demandée"}
    </span>
  );

  return (
    <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-6 space-y-5" data-testid="ms-activation-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <Cloud className="w-5 h-5 text-[#EAB308]" />
          <h2 className="text-lg font-bold text-white">Microsoft Cloud Connect</h2>
        </div>
        <Badge />
      </div>

      {/* NOT_REQUESTED */}
      {status === "NOT_REQUESTED" && (
        <>
          <p className="text-sm text-white/65 leading-relaxed">
            Connectez vos documents Microsoft à PROGÈRE pour centraliser vos plans, photos, rapports, soumissions
            et autres documents de projet. <strong className="text-white">L'activation doit être effectuée par le soutien technique PROGÈRE.</strong>{" "}
            Des frais peuvent s'appliquer selon les services, modules et besoins de votre entreprise.
            <span className="text-white/45"> Aucun frais ne sera facturé sans votre acceptation préalable.</span>
          </p>

          {showDetails && (
            <div className="rounded-md border border-white/10 bg-black/30 p-4 text-sm text-white/70 space-y-1" data-testid="ms-service-details">
              <p className="font-semibold text-white">Services pouvant être connectés selon votre forfait :</p>
              <ul className="list-disc list-inside text-white/60 space-y-0.5">
                <li>Microsoft OneDrive / OneDrive Entreprise / SharePoint</li>
                <li>Synchronisation des documents de projet (plans, photos, rapports, etc.)</li>
                <li>Utilisation des documents dans les modules PROGÈRE sélectionnés</li>
              </ul>
              <p className="text-white/45 text-xs pt-1">Des frais d'activation, de configuration, de maintenance ou d'utilisation peuvent s'appliquer.</p>
            </div>
          )}

          {canManage && (
            <div className="space-y-3">
              <div>
                <div className="text-xs text-white/50 mb-1.5">Modules à connecter</div>
                <div className="flex flex-wrap gap-1.5" data-testid="ms-modules">
                  {MODULES.map((m) => (
                    <button key={m} data-testid={`ms-mod-${m}`} onClick={() => toggle(mods, setMods, m)}
                      className={`rounded-full px-2.5 py-1 text-xs border ${mods.includes(m) ? "bg-[#EAB308] text-black border-[#EAB308]" : "bg-white/5 border-white/15 text-white/60"}`}>
                      {m.replace("_", " ")}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs text-white/50 mb-1.5">Services Microsoft</div>
                <div className="flex flex-wrap gap-1.5" data-testid="ms-services">
                  {SERVICES.map((s) => (
                    <button key={s.v} data-testid={`ms-svc-${s.v}`} onClick={() => toggle(svcs, setSvcs, s.v)}
                      className={`rounded-full px-2.5 py-1 text-xs border ${svcs.includes(s.v) ? "bg-white/15 border-white/30 text-white" : "bg-white/5 border-white/15 text-white/60"}`}>
                      {s.l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {canManage && (
              <Button data-testid="ms-request-btn" onClick={submitRequest} disabled={busy}
                className="h-11 bg-[#EAB308] text-black font-semibold px-5 hover:opacity-90 gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />}
                Demander l'activation Microsoft
              </Button>
            )}
            <a href="/app/support" data-testid="ms-support-btn"
              className="inline-flex items-center gap-2 rounded-md border border-white/15 bg-white/5 px-5 h-11 text-sm text-white/80 hover:bg-white/10">
              <LifeBuoy className="w-4 h-4" /> Communiquer avec le soutien
            </a>
            <Button data-testid="ms-details-btn" onClick={() => setShowDetails((s) => !s)}
              className="h-11 bg-transparent border border-white/15 text-white/70 px-5 gap-2 hover:bg-white/10">
              <Info className="w-4 h-4" /> Voir les détails du service
            </Button>
          </div>
        </>
      )}

      {/* REQUESTED / UNDER_REVIEW */}
      {(status === "REQUESTED" || status === "UNDER_REVIEW" || status === "QUOTE_REQUIRED") && (
        <div className="flex items-start gap-3" data-testid="ms-requested">
          <Clock className="w-5 h-5 text-sky-400 shrink-0 mt-0.5" />
          <div className="text-sm text-white/70">
            <p className="font-semibold text-white">Demande transmise</p>
            <p className="mt-1">Votre demande d'activation Microsoft Cloud Connect a été transmise au soutien technique PROGÈRE.
              Notre équipe communiquera avec vous afin de confirmer vos besoins, les modules à connecter et les frais applicables.</p>
            {act?.ticket_ref && <p className="text-xs text-white/40 mt-2">Référence billet : <span className="font-mono">{act.ticket_ref}</span></p>}
          </div>
        </div>
      )}

      {/* AWAITING_CUSTOMER_APPROVAL */}
      {status === "AWAITING_CUSTOMER_APPROVAL" && (
        <div className="space-y-4" data-testid="ms-fees">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#EAB308] shrink-0 mt-0.5" />
            <div className="text-sm text-white/70">
              <p className="font-semibold text-white">Votre approbation est requise</p>
              <p className="mt-1">Une proposition d'activation Microsoft Cloud Connect est disponible. Veuillez consulter les frais et accepter la proposition pour poursuivre.</p>
            </div>
          </div>
          {act?.customer_message && <p className="text-sm text-white/60 rounded-md border border-white/10 bg-black/30 p-3">{act.customer_message}</p>}
          <div className="grid gap-2 sm:grid-cols-3">
            <div className="rounded-md border border-white/10 bg-black/30 p-3"><div className="text-xs text-white/40">Frais d'activation</div><div className="text-white font-semibold">{(act?.setup_fee || 0).toFixed(2)} $ CAD</div></div>
            <div className="rounded-md border border-white/10 bg-black/30 p-3"><div className="text-xs text-white/40">Frais mensuels</div><div className="text-white font-semibold">{(act?.monthly_fee || 0).toFixed(2)} $ CAD</div></div>
            <div className="rounded-md border border-white/10 bg-black/30 p-3"><div className="text-xs text-white/40">Taux soutien / h</div><div className="text-white font-semibold">{(act?.support_hourly_rate || 0).toFixed(2)} $ CAD</div></div>
          </div>
          {canManage ? (
            <div className="space-y-2.5">
              {[
                ["info", "J'ai pris connaissance des frais applicables."],
                ["setup", "J'accepte les frais d'activation indiqués."],
                ["recurring", "J'accepte les frais récurrents, lorsque ceux-ci s'appliquent."],
                ["authorize", "J'autorise PROGÈRE à activer Microsoft Cloud Connect pour mon entreprise."],
              ].map(([k, label]) => (
                <label key={k} data-testid={`ms-fee-${k}`} className="flex items-start gap-2.5 cursor-pointer">
                  <Checkbox checked={fees[k]} onCheckedChange={(v) => setFees((f) => ({ ...f, [k]: !!v }))}
                    className="mt-0.5 border-white/30 data-[state=checked]:bg-[#EAB308] data-[state=checked]:border-[#EAB308]" />
                  <span className="text-sm text-white/75">{label}</span>
                </label>
              ))}
              <Input data-testid="ms-fee-name" value={fees.name} onChange={(e) => setFees((f) => ({ ...f, name: e.target.value }))}
                placeholder="Nom et confirmation électronique" className="h-10 bg-black/40 border-white/15 text-white max-w-sm" />
              <Button data-testid="ms-accept-fees" onClick={submitFees}
                disabled={busy || !(fees.info && fees.setup && fees.authorize && fees.name.trim() && ((act?.monthly_fee || 0) === 0 || fees.recurring))}
                className="h-11 bg-[#EAB308] text-black font-semibold px-5 hover:opacity-90 disabled:opacity-40 gap-2">
                <CheckCircle2 className="w-4 h-4" /> Accepter et poursuivre
              </Button>
            </div>
          ) : <p className="text-sm text-[#ff8fa3]">Seul le propriétaire ou un administrateur peut accepter les frais.</p>}
        </div>
      )}

      {/* PAYMENT_REQUIRED */}
      {status === "PAYMENT_REQUIRED" && (
        <div className="space-y-3" data-testid="ms-payment">
          <div className="flex items-start gap-3">
            <CreditCard className="w-5 h-5 text-[#EAB308] shrink-0 mt-0.5" />
            <div className="text-sm text-white/70">
              <p className="font-semibold text-white">Paiement requis</p>
              <p className="mt-1">Réglez les frais pour finaliser l'activation. Total à payer : {" "}
                <strong className="text-white">{((act?.setup_fee || 0) + (act?.monthly_fee || 0)).toFixed(2)} $ CAD</strong>
                {(act?.monthly_fee || 0) > 0 ? ` (dont ${(act.monthly_fee).toFixed(2)} $ / mois)` : ""}.</p>
            </div>
          </div>
          {canManage && (
            <Button data-testid="ms-pay-btn" onClick={pay} disabled={busy}
              className="h-11 bg-[#EAB308] text-black font-semibold px-5 hover:opacity-90 gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Payer maintenant
            </Button>
          )}
        </div>
      )}

      {/* APPROVED */}
      {status === "APPROVED" && (
        <div className="flex items-start gap-3" data-testid="ms-approved">
          <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
          <div className="text-sm text-white/70">
            <p className="font-semibold text-white">Paiement reçu</p>
            <p className="mt-1">Le soutien technique PROGÈRE finalise l'activation de votre compte. Vous serez avisé dès qu'elle sera active.</p>
          </div>
        </div>
      )}

      {/* SUSPENDED / REJECTED */}
      {(status === "SUSPENDED" || status === "REJECTED") && (
        <div className="flex items-start gap-3" data-testid="ms-blocked">
          <AlertTriangle className="w-5 h-5 text-[#ff8fa3] shrink-0 mt-0.5" />
          <div className="text-sm text-white/70">
            <p className="font-semibold text-white">{status === "SUSPENDED" ? "Service suspendu" : "Demande refusée"}</p>
            <p className="mt-1">Communiquez avec le soutien technique PROGÈRE pour plus d'informations.</p>
          </div>
        </div>
      )}

      <div className="flex items-start gap-2.5 rounded-md border border-white/10 bg-white/[0.02] p-3">
        <ShieldCheck className="w-4 h-4 text-[#EAB308] shrink-0 mt-0.5" />
        <p className="text-xs text-white/55">
          PROGÈRE n'est pas hébergé sur Azure. Microsoft Entra est utilisé uniquement pour autoriser sécuritairement
          la connexion à votre OneDrive. Vos fichiers demeurent dans votre propre compte Microsoft.
        </p>
      </div>
    </div>
  );
}
